"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-12">
          <Link
            href="/support"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Support
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">Last updated: January 1, 2025</p>
        </header>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              1. Introduction
            </h2>
            <p className="text-muted-foreground mb-4">
              Donezo (hereinafter referred to as &quot;the App&quot;) respects user privacy and is committed to protecting personal information. This Privacy Policy explains what information the App collects and how it is used.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              2. Information We Collect
            </h2>
            <p className="text-muted-foreground mb-4">
              The App may collect the following information:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Account information (email address)</li>
              <li>
                Task data (tasks, notes, and notebook contents you create)
              </li>
              <li>Usage data (app usage frequency, feature utilization)</li>
              <li>Device information (OS version, app version)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              3. How We Use Information
            </h2>
            <p className="text-muted-foreground mb-4">
              The information we collect is used for the following purposes:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Providing and improving the App&apos;s services and features</li>
              <li>Providing user support</li>
              <li>Syncing data across multiple devices</li>
              <li>Ensuring security and preventing unauthorized use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              4. Data Storage and Protection
            </h2>
            <p className="text-muted-foreground mb-4">
              User data is stored on secure cloud servers through encrypted communication. Backups are also saved locally on your device, allowing you to use the app offline.
            </p>
            <p className="text-muted-foreground">
              We implement appropriate security measures to protect data from unauthorized access, loss, or destruction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              5. Disclosure to Third Parties
            </h2>
            <p className="text-muted-foreground mb-4">
              We do not share your personal information with third parties except in the following cases:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>With your consent</li>
              <li>When required by law</li>
              <li>
                To service providers necessary for delivering our services (under confidentiality agreements)
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              6. Your Rights
            </h2>
            <p className="text-muted-foreground mb-4">
              Users have the following rights:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Access to your personal information</li>
              <li>Request correction or deletion of personal information</li>
              <li>Delete your account</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              To exercise these rights, please contact us through the support page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              7. Cookies and Tracking
            </h2>
            <p className="text-muted-foreground">
              The App may use cookies for session management. These are technical cookies necessary for providing the service, and we do not perform tracking for advertising purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              8. Children&apos;s Privacy
            </h2>
            <p className="text-muted-foreground">
              The App is not intended for children under 13 years of age. We do not intentionally collect personal information from children under 13.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              9. Changes to Privacy Policy
            </h2>
            <p className="text-muted-foreground">
              This Privacy Policy may be updated as needed. If there are significant changes, we will notify you through the app or by email.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              10. Contact Us
            </h2>
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy, please contact us at{" "}
              <a
                href="mailto:n.shinzo1010@icloud.com"
                className="text-accent-300 hover:underline"
              >
                n.shinzo1010@icloud.com
              </a>
              .
            </p>
          </section>
        </div>

        {/* Footer */}
        <footer className="pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>&copy; 2025 Donezo. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
